import React, {useCallback, useContext, useEffect, useState} from "react"
import Row from "@amzn/meridian/row";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import {RouteContext} from "./context/RouteContext";
import Pagination from "@amzn/meridian/pagination";
import Input from "@amzn/meridian/input";
import {getRefreshInterval} from "../util/Util";
import {FormattedMessage} from "react-intl";
import {CapabilityContext} from "../global/context/CapabilityContext";
import {RouteFilterContext} from "./context/RouteFilterContext";
import {AR_V2} from "../util/Constants";

const RouteTable = () => {
    const { viewModel, dispatch } = useContext(RouteContext)
    const { configViewModel } = useContext(CapabilityContext)
    const { viewModelRouteFilter } = useContext(RouteFilterContext)
    let routeList = viewModel.routeList;

    /** Pagination State **/
    const [currentPage, setCurrentPage] = useState(1)

    /** Sorting **/
    const [sortColumn, setSortColumn] = useState("routeCode")
    const [sortDirection, setSortDirection] = useState("ascending")
    const onSort = useCallback(({ sortColumn, sortDirection }) => {
        setSortDirection(sortDirection)
        setSortColumn(sortColumn)
    }, [])

    routeList.sort((a, b) => {
        if (a[sortColumn] < b[sortColumn]) {
            return sortDirection === "ascending" ? -1 : 1
        }
        if (a[sortColumn] > b[sortColumn]) {
            return sortDirection === "ascending" ? 1 : -1
        }
        return 0
    })

    /** In Line Table Filter **/
    const [routeCodeFilter, setRouteCodeFilter] = useState("")
    const [routeSLAFilter, setRouteSLAFilter] = useState("")
    const [inductedFilter, setInductedFilter] = useState("")
    const [plannedFilter, setPlannedFilter] = useState("")
    const [stowedFilter, setStowedFilter] = useState("")
    const [pickedFilter, setPickedFilter] = useState("")
    const [stagedFilter, setStagedFilter] = useState("")
    const [departedFilter, setDepartedFilter] = useState("")
    const [problemSolveFilter, setProblemSolveFilter] = useState("")

    /** Filter **/
    const inLineFilter = (route, filter, key) => {
        return filter.length === 0 || (route[key] && route[key].toString().toLowerCase().startsWith(filter.toLowerCase()));
    }

    routeList = routeList
        .filter(route => inLineFilter(route, routeCodeFilter, "routeCode"))
        .filter(route => inLineFilter(route, routeSLAFilter, "routeSLA"))
        .filter(route => inLineFilter(route, inductedFilter, "inducted"))
        .filter(route => inLineFilter(route, plannedFilter, "planned"))
        .filter(route => inLineFilter(route, stowedFilter, "stowed"))
        .filter(route => inLineFilter(route, pickedFilter, "stowed"))
        .filter(route => inLineFilter(route, stagedFilter, "staged"))
        .filter(route => inLineFilter(route, departedFilter, "departed"))
        .filter(route => inLineFilter(route, problemSolveFilter, "problemSolve"))


    const itemOnPage = 50;
    let pageSize = Math.ceil(routeList.length / itemOnPage);
    let startPage = (currentPage * 50) - itemOnPage;
    let endPage = (startPage + itemOnPage) > routeList.length ? routeList.length :
        (startPage + itemOnPage);

    const getRouteData = () => {
        const isCore = (viewModelRouteFilter.selectedRouteType[0] === 'Core')
        dispatch({
            type: 'LOAD_VIEW', data: {
                dispatcher: dispatch,
                isCore: isCore,
                baseUrl: configViewModel.baseUrl,
                capability: configViewModel.capability,
                stationConfig: configViewModel.stationConfig
            }
        })
    }

    useEffect(() => {
        getRouteData();
        const interval = setInterval(() => {
            getRouteData();
        }, getRefreshInterval(configViewModel.stationConfig));
        return () => clearInterval(interval);
    }, [viewModelRouteFilter]);

    const getTableHeader = () => {
        if (viewModelRouteFilter.selectedRouteType[0] === 'Non-Core') {
            return (
                <TableRow>
                    <TableCell sortColumn="routeCode">Route</TableCell>
                    <TableCell sortColumn="routeSLA">SLA</TableCell>
                    <TableCell sortColumn="planned" alignmentHorizontal="right">
                        <FormattedMessage id="routeTable.planned" defaultMessage="Planned"/>
                    </TableCell>
                    <TableCell sortColumn="inducted" alignmentHorizontal="right">
                        <FormattedMessage id="routeTable.inducted" defaultMessage="Inducted"/>
                    </TableCell>
                    <TableCell sortColumn="stowed" alignmentHorizontal="right">
                        <FormattedMessage id="routeTable.stowed" defaultMessage="Stowed"/>
                    </TableCell>
                    <TableCell sortColumn="departed" alignmentHorizontal="right">
                        <FormattedMessage id="routeTable.departed" defaultMessage="Departed"/>
                    </TableCell>
                    <TableCell sortColumn="problemSolve" alignmentHorizontal="right">
                        <FormattedMessage id="routeTable.ps" defaultMessage="PS"/>
                    </TableCell>
                </TableRow>
            )
        } else {
            return (
                <TableRow>
                    <TableCell sortColumn="routeCode">Route</TableCell>
                    <TableCell sortColumn="routeSLA">SLA</TableCell>
                    <TableCell sortColumn="inducted" alignmentHorizontal="right">
                        <FormattedMessage id="routeTable.inducted" defaultMessage="Inducted"/>
                    </TableCell>
                    {configViewModel.capability === AR_V2 ?
                        [
                            <TableCell sortColumn="picked" alignmentHorizontal="right">
                                <FormattedMessage id="routeTable.picked" defaultMessage="Picked"/>
                            </TableCell>
                            ,
                            <TableCell sortColumn="stowed" alignmentHorizontal="right">
                                <FormattedMessage id="routeTable.stowed" defaultMessage="Stowed"/>
                            </TableCell>
                        ] : [
                            <TableCell sortColumn="stowed" alignmentHorizontal="right">
                                <FormattedMessage id="routeTable.stowed" defaultMessage="Stowed"/>
                            </TableCell>,
                            <TableCell sortColumn="picked" alignmentHorizontal="right">
                                <FormattedMessage id="routeTable.picked" defaultMessage="Picked"/>
                            </TableCell>
                        ]
                    }
                    <TableCell sortColumn="staged" alignmentHorizontal="right">
                        <FormattedMessage id="routeTable.staged" defaultMessage="Staged"/>
                    </TableCell>
                    <TableCell sortColumn="departed" alignmentHorizontal="right">
                        <FormattedMessage id="routeTable.departed" defaultMessage="Departed"/>
                    </TableCell>
                    <TableCell sortColumn="problemSolve" alignmentHorizontal="right">
                        <FormattedMessage id="routeTable.ps" defaultMessage="PS"/>
                    </TableCell>
                </TableRow>
            )
        }
    }

    const getTableFilter = () => {
        if (viewModelRouteFilter.selectedRouteType[0] === 'Non-Core') {
            return (
                <TableRow>
                    <TableCell>
                        <Input type="text" size="small" value={routeCodeFilter} onChange={setRouteCodeFilter}/>
                    </TableCell>
                    <TableCell>
                        <Input type="text" size="small" value={routeSLAFilter} onChange={setRouteSLAFilter}/>
                    </TableCell>
                    <TableCell>
                        <Input type="text" size="small" value={plannedFilter} onChange={setPlannedFilter}/>
                    </TableCell>
                    <TableCell>
                        <Input type="text" size="small" value={inductedFilter} onChange={setInductedFilter}/>
                    </TableCell>
                    <TableCell>
                        <Input type="text" size="small" value={stowedFilter} onChange={setStowedFilter}/>
                    </TableCell>
                    <TableCell>
                        <Input type="text" size="small" value={departedFilter} onChange={setDepartedFilter}/>
                    </TableCell>
                    <TableCell>
                        <Input type="text" size="small" value={problemSolveFilter} onChange={setProblemSolveFilter}/>
                    </TableCell>
                </TableRow>
            )
        } else {
            return (
                <TableRow>
                    <TableCell>
                        <Input type="text" size="small" value={routeCodeFilter} onChange={setRouteCodeFilter}/>
                    </TableCell>
                    <TableCell>
                        <Input type="text" size="small" value={routeSLAFilter} onChange={setRouteSLAFilter}/>
                    </TableCell>
                    <TableCell>
                        <Input type="text" size="small" value={inductedFilter} onChange={setInductedFilter}/>
                    </TableCell>
                    {configViewModel.capability === AR_V2 ?
                        [
                            <TableCell>
                                <Input type="text" size="small" value={stowedFilter} onChange={setStowedFilter}/>
                            </TableCell>,
                            <TableCell>
                                <Input type="text" size="small" value={pickedFilter} onChange={setPickedFilter}/>
                            </TableCell>
                        ] : [
                            <TableCell>
                                <Input type="text" size="small" value={pickedFilter} onChange={setPickedFilter}/>
                            </TableCell>,
                            <TableCell>
                                <Input type="text" size="small" value={stowedFilter} onChange={setStowedFilter}/>
                            </TableCell>
                        ]
                    }
                    <TableCell>
                        <Input type="text" size="small" value={stagedFilter} onChange={setStagedFilter}/>
                    </TableCell>
                    <TableCell>
                        <Input type="text" size="small" value={departedFilter} onChange={setDepartedFilter}/>
                    </TableCell>
                    <TableCell>
                        <Input type="text" size="small" value={problemSolveFilter} onChange={setProblemSolveFilter}/>
                    </TableCell>
                </TableRow>
            )
        }
    }

    const getTableRows = () => {
        return routeList.map((route, key) => {
            if (key >= startPage && key <= endPage) {
                if (viewModelRouteFilter.selectedRouteType[0] === 'Non-Core') {
                    return (
                        <TableRow key={route.routeCode}>
                            <TableCell>{route.routeCode}</TableCell>
                            <TableCell>{route.routeSLA}</TableCell>
                            <TableCell
                                alignmentHorizontal="right">{route.planned === -1 ? '-' : route.planned}</TableCell>
                            <TableCell
                                alignmentHorizontal="right">{route.inducted === -1 ? '-' : route.inducted}</TableCell>
                            <TableCell
                                alignmentHorizontal="right">{route.stowed === -1 ? '-' : route.stowed}</TableCell>
                            <TableCell
                                alignmentHorizontal="right">{route.departed === -1 ? '-' : route.departed}</TableCell>
                            <TableCell
                                alignmentHorizontal="right">{route.problemSolve === -1 ? '-' : route.problemSolve}</TableCell>
                        </TableRow>
                    )
                } else {
                    return (
                        <TableRow key={route.routeCode}>
                            <TableCell>{route.routeCode}</TableCell>
                            <TableCell>{route.routeSLA}</TableCell>
                            <TableCell
                                alignmentHorizontal="right">{route.inducted === -1 ? '-' : route.inducted}</TableCell>

                            {configViewModel.capability === AR_V2 ?
                                [
                                    <TableCell
                                        alignmentHorizontal="right">{route.picked === -1 ? '-' : route.picked}</TableCell>,
                                    <TableCell
                                        alignmentHorizontal="right">{route.stowed === -1 ? '-' : route.stowed}</TableCell>

                                ] : [
                                    <TableCell
                                        alignmentHorizontal="right">{route.stowed === -1 ? '-' : route.stowed}</TableCell>,
                                    <TableCell
                                        alignmentHorizontal="right">{route.picked === -1 ? '-' : route.picked}</TableCell>
                                ]
                            }
                            <TableCell
                                alignmentHorizontal="right">{route.staged === -1 ? '-' : route.staged}</TableCell>
                            <TableCell
                                alignmentHorizontal="right">{route.departed === -1 ? '-' : route.departed}</TableCell>
                            <TableCell
                                alignmentHorizontal="right">{route.problemSolve === -1 ? '-' : route.problemSolve}</TableCell>
                        </TableRow>
                    )
                }
            }
            return null;
        });
    }

    return (
        <div>
            <Row widths="100%" spacingInset="medium large medium large">
                <Table headerRows={1} spacing="small" sortColumn={sortColumn} sortDirection={sortDirection}
                       onSort={onSort}>
                    {getTableHeader()}
                    {getTableFilter()}
                    {getTableRows()}
                </Table>
            </Row>
            {routeList.length > 0 ?
                <Row width="100%" alignmentHorizontal="center" spacingInset="medium large medium large">
                    <Pagination
                        numberOfPages={pageSize}
                        onChange={setCurrentPage}
                        currentPage={currentPage}/>
                </Row>
                : null
            }
        </div>
    )
}

export default RouteTable


// WEBPACK FOOTER //
// ./src/components/routeView/RouteTable.js